import { MinsterConfirmDialogComponent } from '../ui-components/minster-confirm-dialog/minster-confirm-dialog.component';
import { MinsterShowHideComponent } from "../ui-components/minster-show-hide/minster-show-hide.component";
import { MinsterCheckboxComponent } from "../ui-components/minster-checkbox/minster-checkbox.component";
import { MinsterSelectComponent } from "../ui-components/minster-select/minster-select.component";
import { ControlContainer, FormsModule, NgForm, NgModelGroup, Validators } from '@angular/forms';
import { MinsterInputComponent } from "../ui-components/minster-input/minster-input.component";
import { MinsterCardComponent } from "../ui-components/minster-card/minster-card.component";
import { MinsterTelComponent } from '../ui-components/minster-tel/minster-tel.component';
import { ScreenBaseComponent } from '../screen-base/screen-base.component';
import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { ScreenType, InputType } from '../../models/enums.models';
import { MatExpansionModule } from '@angular/material/expansion';
import { DialogModel } from '../../models/dialog-model.model';
import { ClaimsService } from '../../services/claims.service';
import { StateService } from '../../services/state.service';
import { MatButtonModule } from '@angular/material/button';
import { Claim, Witness } from '../../models/claim.model';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { LookUp } from '../../models/look-up.model';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

@Component({
    selector: 'app-police-and-witnesses',
    standalone: true,
    imports: [MinsterCardComponent, FormsModule, MinsterSelectComponent, MinsterShowHideComponent, MinsterInputComponent,
        MinsterCheckboxComponent, MatButtonModule, MatExpansionModule, MatIconModule, MinsterTelComponent],
    templateUrl: './police-and-witnesses.component.html',
    styleUrl: './police-and-witnesses.component.css',
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class PoliceAndWitnessesComponent extends ScreenBaseComponent {
    @ViewChild(NgModelGroup) _formGroup: NgModelGroup | undefined;

    _claim: Claim;
    _inputTypes = InputType;
    _policeInvolvement: LookUp[];
    _policeJurisdiction: LookUp[];
    _haveWitnesses: boolean; 
    _titles: LookUp[];
    _witnesses: Witness[]; 
    _telTypes: LookUp[];
    _readonly: boolean = false;

    constructor(
        stateService: StateService,
        router: Router,
        toastr: ToastrService,
        private dialog: MatDialog,
        claimService: ClaimsService,
        private change: ChangeDetectorRef,
    ) {
        super(router, ScreenType.PoliceAndWitnesses, toastr, stateService, claimService);

        this._claim = stateService.claim!
        this._readonly = stateService.formIsSubmitted;
        this._witnesses = this._claim.witnesses!;
        this._haveWitnesses = stateService.claim!.witnesses.length > 0;
        this._policeInvolvement = stateService.lookUps!.lookUpDictionary['PoliceInvolvement']
        this._policeJurisdiction = stateService.lookUps!.lookUpDictionary['PoliceJurisdiction']
        this._titles = stateService.lookUps?.lookUpDictionary['Title']!;
        this._telTypes = stateService.lookUps?.lookUpDictionary['TelephoneNumberType']!;
    }

    override isScreenValid(): boolean {
        return this.stateService.getFormValidationErrorsExcludeRequired(this._formGroup!.control) == 0;
    }

    onAddWitness(): void {
        if (this._witnesses.length >= 10) {
            this.toastr.warning('You can only add up to 10 witnesses.');
            return;
        }

        const newWitness = this.createNewPassenger(this.stateService.claim!.claimDetailId);
        this.stateService.claim?.witnesses.push(newWitness);
        this._witnesses = this.stateService.claim?.witnesses || [];
        this.change.markForCheck();
    }

    createNewPassenger(claimId: number): Witness {
        return {
            accidentWitnessId: 0,
            titleId: 0,
            claimId: claimId,
            forename: '',
            surname: '',
            emailAddress: '',
            contactDetails: [{ telephoneNumber: '', accidentWitnessContactDetailId: 0, isPrimary: true, telephoneNumberTypeId: 1, accidentWitnessId: 0, }]
        }
    }

    onRemoveWitness(witnessIndex: number): void {
        const witnessToRemove = this._claim.witnesses[witnessIndex];
        if (!witnessToRemove) {
            console.warn('Witness not found.');
            return;
        }

        const message = `Are you sure you want to delete the witness ${witnessToRemove.forename} ${witnessToRemove.surname}?`;
        this.dialog
            .open(MinsterConfirmDialogComponent, {
                maxWidth: '600px',
                data: new DialogModel('Confirm Deletion', message),
            })
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    this.stateService.claim!.witnesses = this.stateService.claim!.witnesses.filter(
                        (witness) => witness !== witnessToRemove
                    );
                    this._witnesses = this.stateService.claim!.witnesses;
                    this.change.markForCheck();
                }
            });
    }

    onWitnessesChanged(val: boolean): void {
        if (!val) {
            this.stateService.claim!.witnesses = [];
            this._witnesses = [];
            this.toastr.info('All witnesses have been removed as "No independent witnesses" was selected.');
        }
        
        this._haveWitnesses = val;
        this.change.markForCheck();
    }

    get policeInvolved(): boolean {
        return this._claim.policeInvolvementId == 0 || this._claim.policeInvolvementId == 2
    }

    get isRequired(): boolean {
        return this.stateService.formIsSubmitted;
    }

    override onNext(): void {
        if(this._readonly){
            super.onNext();
            return;
        }
        let hasErrors = false;
        this.updateValidators();

        if (this._formGroup && this._formGroup.invalid) {
            Object.keys(this._formGroup.control.controls).forEach((key) => {
                const control = this._formGroup!.control.get(key);

                if (control) {
                    const errors = control.errors;

                    if (errors) {
                        const hasOnlyRequiredError = Object.keys(errors).length === 1 && errors['required'];

                        if (this.stateService.formIsSubmitted || !hasOnlyRequiredError) {
                            hasErrors = true;
                        }
                    }

                    control.markAsTouched();
                    control.markAsDirty();
                    control.updateValueAndValidity();
                }
            });

            if (hasErrors) {
                this.toastr.warning('Please fix the form errors before proceeding.');
                this.stateService.enableSaving = false;
                return;
            }
        }

        if (this.stateService.formIsSubmitted && this._formGroup!.invalid) {
            this.toastr.warning('Please fix the required fields before proceeding.');
            this.stateService.enableSaving = false;
            return;
        }

        //Trigger a save manually, as it's the last form screen
        this.stateService.enableSaving = this.hasStateChanged();

        this.canMoveToNextRoute()
            .then(() => {
                super.onNext();
            })
            .catch(() => {
                this.toastr.error('Unable to save the form. Please fix the errors and try again.');
            });
    }

    updateValidators(): void {
        const controls = [
            { formName: this._fcn.PoliceAndWitness.Station, makeRequired: this.policeInvolved, resetValue: '' },
            { formName: this._fcn.PoliceAndWitness.PoliceJurisdiction, makeRequired: this.policeInvolved, resetValue: null },
            { formName: this._fcn.PoliceAndWitness.ReferenceNumber, makeRequired: this.policeInvolved, resetValue: '' }
        ];

        controls.forEach(({ formName, makeRequired, resetValue }) => {
            const control = this._formGroup?.control.get(formName);

            if (control) {
                if (makeRequired && !control.hasValidator(Validators.required)) {
                    control.setValidators([Validators.required]);
                }
                else if (!makeRequired && control.validator) {
                    control.clearValidators();
                    control.setValue(resetValue);
                }

                control.updateValueAndValidity();
            }
        });
    }

    onAddPhoneNumber(witness: Witness): void {
        if (!witness.contactDetails || witness.contactDetails.length === 0) {
            witness.contactDetails = [
                {
                    accidentWitnessContactDetailId: 0,
                    accidentWitnessId: 0,
                    telephoneNumberTypeId: 2,
                    telephoneNumber: '',
                    isPrimary: false,
                    updatedBy: ''
                }
            ];
            return;
        }

        if (witness.contactDetails.length >= 2) {
            this.toastr.warning('You can only add one additional phone number.');
            return;
        }

        witness.contactDetails.push({
            accidentWitnessContactDetailId: 0,
            accidentWitnessId: 0,
            telephoneNumberTypeId: 2,
            telephoneNumber: '',
            isPrimary: false,
            updatedBy: ''
        });
    }

    onRemoveSecondaryPhoneNumber(witness: Witness): void {
        if (witness.contactDetails!.length > 1) {
            witness.contactDetails!.splice(1, 1);
        }
        else {
            this.toastr.warning('You cannot remove the primary phone number.');
        }
    }

    canAddPhone(witness: Witness): boolean {
        const contactDetails = witness.contactDetails ?? [];
        return contactDetails.length < 2 && this.isPhoneNumberValid(contactDetails[0]?.telephoneNumber, contactDetails[0]?.telephoneNumberTypeId);
    }
    
    private isPhoneNumberValid(phoneNumber: string, type: number): boolean {
        const phoneRegex = type === 1 ? /^07[0-9]{8,15}$/ : /^(?:01|02|03|08)[0-9]{8,15}$/;
        return phoneRegex.test(phoneNumber) && phoneNumber?.trim().length > 0;
    }

    getPrimaryTelephoneNumber(index: number): string {
        return this._witnesses[index]?.contactDetails?.[0]?.telephoneNumber || '';
    }

    setPrimaryTelephoneNumber(index: number, value: string): void {
        const passengers = this._witnesses[index];

        if (passengers?.contactDetails?.[0]) {
            passengers.contactDetails[0].telephoneNumber = value;
            this.updatePrimaryStatus(index);
        }
    }

    getPrimaryPhoneType(index: number): 'mobile' | 'landline' {
        return this.getPrimaryTelephoneTypeId(index) === 1 ? 'mobile' : 'landline';
    }

    getPrimaryTelephoneTypeId(index: number): number {
        return this._witnesses[index]?.contactDetails?.[0]?.telephoneNumberTypeId || 1;
    }

    setPrimaryTelephoneTypeId(index: number, value: number): void {
        const thirdParty = this._witnesses[index];

        if (thirdParty?.contactDetails?.[0]) {
            thirdParty.contactDetails[0].telephoneNumberTypeId = value;
            this.updatePrimaryStatus(index);
        }
    }

    getSecondaryTelephoneNumber(index: number): string {
        return this._witnesses[index]?.contactDetails?.[1]?.telephoneNumber || '';
    }

    setSecondaryTelephoneNumber(index: number, value: string): void {
        const thirdParty = this._witnesses[index];

        if (thirdParty?.contactDetails?.[1]) {
            thirdParty.contactDetails[1].telephoneNumber = value;
            this.updatePrimaryStatus(index);
        }
    }

    getSecondaryPhoneType(index: number): 'mobile' | 'landline' {
        return this.getSecondaryTelephoneTypeId(index) === 1 ? 'mobile' : 'landline';
    }

    getSecondaryTelephoneTypeId(index: number): number {
        return this._witnesses[index]?.contactDetails?.[1]?.telephoneNumberTypeId || 1;
    }

    setSecondaryTelephoneTypeId(index: number, value: number): void {
        const thirdParty = this._witnesses[index];

        if (thirdParty?.contactDetails?.[1]) {
            thirdParty.contactDetails[1].telephoneNumberTypeId = value;
            this.updatePrimaryStatus(index);
        }
    }

    canAddPhoneAdditionalTp(index: number): boolean {
        const contactDetails = this._witnesses[index].contactDetails ?? [];
        return contactDetails.length < 2 && this.isPhoneNumberValid(contactDetails[0]?.telephoneNumber, contactDetails[0]?.telephoneNumberTypeId);
    }

    private updatePrimaryStatus(index?: number): void {
        let contactDetails = this._witnesses[index ?? 0]?.contactDetails;

        if (!contactDetails) {
            return;
        }

        const primary = contactDetails[0];
        const secondary = contactDetails[1];

        if (primary) {
            primary.isPrimary = true;
        }

        if (secondary) {
            secondary.isPrimary = primary?.telephoneNumberTypeId !== secondary.telephoneNumberTypeId;
        }
    }
}
